import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { formatDateTime } from "@/lib/format-date";
import { userErrorMessage } from "@/lib/user-error";
import { AutoMatchCompanyNamesDocument, MatchAutoRunStatus } from "@/graphql/generated/operations";
import { enabledByText, needsRematch, runReasonLabel, runStatusLabel, type AutoRun } from "./auto-match-runs";
import { useMatchAutoMatch } from "./use-match-auto-match";

// Die neuesten zuerst, die übrigen erst auf Wunsch: bis zu 50 Läufe am Tag sind möglich.
const VISIBLE_RUNS = 5;

/**
 * "Automatische Bewertungen": je Bewerbung ein Lauf, mit Person, Zustand und Grund, und dort, wo genesis
 * es erlaubt, "Erneut versuchen". Steht unter den gespeicherten Ergebnissen, weil seine Läufe genau
 * dorthin anhängen. Erscheint, sobald es Läufe gibt, und sonst nur, wenn der Schalter an ist und das
 * Match an einer Ausschreibung hängt, die die Person sieht: ohne die läuft nichts.
 *
 * Bei geänderten Anforderungen bietet die Zeile kein "Erneut versuchen" an, obwohl genesis es erlaubt: der
 * Lauf endete wieder so, solange kein Ergebnis mit den neuen Anforderungen gespeichert ist, und kostete
 * trotzdem einen KI-Lauf. Sie verweist stattdessen auf den Neulauf darüber (`onRematch`).
 */
export function AutoMatchRunsBox({
  matchId,
  latestSavedResultAt,
  onRematch,
}: {
  matchId: string;
  /** Wann das neueste gespeicherte Ergebnis entstand; ein späteres erledigt "neu matchen". */
  latestSavedResultAt: string | null;
  onRematch?: () => void;
}) {
  const intl = useIntl();
  const auto = useMatchAutoMatch(matchId, { poll: true });
  const companyIds = [...new Set(auto.runs.flatMap(run => run.companyId ? [run.companyId] : []))].sort();
  const names = useQuery(AutoMatchCompanyNamesDocument, {
    variables: { input: { ids: companyIds } }, skip: companyIds.length === 0,
  });
  const companyNames = new Map(names.data?.companyIdentities.companies.map(company => [company.id, company.name]));
  const [showAll, setShowAll] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (auto.loading || auto.error) return null;
  if (!auto.relevant || (!auto.autoMatch && auto.runs.length === 0)) return null;

  async function retry(run: AutoRun) {
    setRetrying(run.id);
    setError(null);
    try {
      await auto.retry(run.id);
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setRetrying(null);
    }
  }

  const shown = showAll ? auto.runs : auto.runs.slice(0, VISIBLE_RUNS);
  const hidden = auto.runs.length - shown.length;

  return (
    <div className="box bg-surface p-5">
      <BoxHeader
        title={<FormattedMessage id="match.autoMatch.runsTitle" defaultMessage="Automatische Bewertungen" />}
        right={<span className="text-smycles-label text-ink-muted tabular-nums">{auto.runs.length}</span>}
      />
      <p className="smycles-field-meta text-ink-muted">
        {auto.autoMatch
          ? enabledByText(intl, auto.autoMatch)
          : intl.formatMessage({
              id: "match.autoMatch.off",
              defaultMessage: "Ausgeschaltet: neue Bewerbungen werden nicht mehr bewertet.",
            })}
      </p>

      {auto.runs.length === 0 ? (
        <p className="smycles-field-meta mt-3 text-ink-muted">
          <FormattedMessage
            id="match.autoMatch.noRuns"
            defaultMessage="Noch keine automatische Bewertung. Vorhandene und neue Bewerbungen werden automatisch übernommen."
          />
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-ink/5">
          {shown.map((run) => {
            const reason = runReasonLabel(run.reason);
            const designChanged = run.status === MatchAutoRunStatus.DesignChanged;
            const rematchPending = needsRematch(run, latestSavedResultAt);
            const meta = [
              intl.formatMessage(
                designChanged && !rematchPending
                  ? { id: "match.autoMatch.status.designChangedSince", defaultMessage: "Anforderungen geändert, seitdem neu gespeichert" }
                  : runStatusLabel(run),
              ),
              reason ? intl.formatMessage(reason) : null,
              formatDateTime(run.createdAt, intl.locale),
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <li key={run.id} className="flex items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-smycles-meta font-medium">
                    {run.companyId
                      ? companyNames.get(run.companyId) || intl.formatMessage({ id: "match.autoMatch.unnamedCompany" })
                      : run.person?.formattedName || intl.formatMessage({
                          id: "match.autoMatch.unnamedPerson", defaultMessage: "Person ohne Namen",
                        })}
                  </div>
                  <div className={`text-smycles-micro ${rematchPending ? "text-ink" : "text-ink-muted"}`}>{meta}</div>
                </div>
                {rematchPending && onRematch ? (
                  <Button variant="ghost" size="sm" onClick={onRematch}>
                    <FormattedMessage id="match.autoMatch.rematch" defaultMessage="Neu matchen" />
                  </Button>
                ) : run.retryable && !designChanged ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={retrying !== null}
                    onClick={() => void retry(run)}
                    title={intl.formatMessage({
                      id: "match.autoMatch.retryTitle",
                      defaultMessage: "Läuft in deinem Namen, eine Bewertung",
                    })}
                  >
                    {retrying === run.id ? (
                      <FormattedMessage id="match.autoMatch.retrying" defaultMessage="Läuft …" />
                    ) : (
                      <FormattedMessage id="match.autoMatch.retry" defaultMessage="Erneut versuchen" />
                    )}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {hidden > 0 ? (
        <Button variant="ghost" size="sm" className="-ml-3 mt-1" onClick={() => setShowAll(true)}>
          <FormattedMessage
            id="match.autoMatch.showAll"
            defaultMessage="Alle {count} anzeigen"
            values={{ count: auto.runs.length }}
          />
        </Button>
      ) : null}

      {error ? (
        <p role="alert" className="smycles-field-meta mt-2 text-ink">
          {error}
        </p>
      ) : null}
    </div>
  );
}
